/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/**
 * Environment configuration interface
 * Similar to Angular's environment pattern but adapted for React
 */
export interface Environment {
  name: 'local' | 'dev' | 'prod';
  production: boolean;
  auth: {
    clientId: string;
    authority: string;
    scopes: {
      graph: string[];
      api: string[];
    };
  };
  api: {
    baseUrl: string;
    endpoints: {
      spreadsheets: string;
      discussions: string;
      statusItems: string;
      clientDocuments: string;
      tasks: string;
      xeroIntegration: string;
      cellChanges: string;
      clients: string;
      users: string;
      jobTemplates: string;
    };
  };
  urls: {
    redirectUri: string;
    digitalHub: string;
    /**
     * Base origin the Pop out button opens against. In dev/prod this is the
     * BDO-branded custom domain (e.g. https://wi.bdo.co.nz) so users see a
     * clean URL in the new tab even when Excel loaded the raw SWA hostname.
     * Locally it's the current runtime origin so localhost dev keeps working.
     */
    standaloneOrigin: string;
  };
  appInsightsInstrumentationKey?: string;
  /** Semver (e.g. "1.0.1") set by CI from the VERSION file, or "dev" locally. */
  version: string;
}

// These will be injected by webpack DefinePlugin during build
declare const ENVIRONMENT_NAME: string | undefined;
declare const API_BASE_URL: string | undefined;
declare const ADDIN_URL: string | undefined;
declare const APPINSIGHTS_KEY: string | undefined;
declare const AZURE_AD_CLIENT_ID: string | undefined;
declare const AZURE_AD_API_CLIENT_ID: string | undefined;
declare const APP_VERSION: string | undefined;

/**
 * Determines the current environment
 */
function getEnvironmentName(): 'local' | 'dev' | 'prod' {
  // If injected at build time, use that
  if (typeof ENVIRONMENT_NAME !== 'undefined' && ENVIRONMENT_NAME) {
    return ENVIRONMENT_NAME as 'local' | 'dev' | 'prod';
  }

  // Otherwise, detect based on hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'local';
    }

    // Check for known deployment URLs
    // Dev environment (these will be the actual Azure Static Web App URLs)
    if (hostname.includes('-dev.') || hostname.includes('ashy-glacier')) {
      return 'dev';
    }

    // Production environment
    if (hostname.includes('-prod.') || hostname.includes('gray-field')) {
      return 'prod';
    }
  }

  // Default to local for safety
  return 'local';
}

/**
 * Gets the current environment configuration
 * This is the main export that services should import
 */
function getEnvironment(): Environment {
  const envName = getEnvironmentName();
  const isLocal = envName === 'local';
  const isDev = envName === 'dev';
  const isProd = envName === 'prod';

  // Falls back to "dev" for local builds where the env var isn't injected.
  const appVersion = typeof APP_VERSION !== 'undefined' && APP_VERSION
    ? APP_VERSION
    : 'dev';

  // Azure AD Client IDs - injected at build time by webpack DefinePlugin
  // These are validated at build time, so they should always be present
  const clientId = typeof AZURE_AD_CLIENT_ID !== 'undefined' && AZURE_AD_CLIENT_ID
    ? AZURE_AD_CLIENT_ID
    : '';

  const apiClientId = typeof AZURE_AD_API_CLIENT_ID !== 'undefined' && AZURE_AD_API_CLIENT_ID
    ? AZURE_AD_API_CLIENT_ID
    : '';

  // Runtime validation - these should never be empty due to build-time validation
  // If they are, it indicates a build configuration issue
  if (!clientId || !apiClientId) {
    const errorMsg = 'FATAL: Missing Azure AD configuration. This should have been caught at build time. Please check webpack configuration.';
    console.error(errorMsg);
    throw new Error(errorMsg);
  }

  // Base configurations
  const baseConfig = {
    name: envName,
    production: isProd,
    version: appVersion,
    auth: {
      clientId,
      authority: 'https://login.microsoftonline.com/common',
      scopes: {
        graph: ['User.Read', 'User.ReadBasic.All'], // TODO: Change to User.Read.All once Aaron has given admin consent
        api: [`api://${apiClientId}/access_as_user`]
      }
    },
    api: {
      endpoints: {
        /* Add endpoints here */
        spreadsheets: '/api/spreadsheets',
        discussions: '/api/discussions',
        statusItems: '/api/status-items',
        clientDocuments: '/api/client-documents',
        tasks: '/api/tasks',
        xeroIntegration: '/api/xero-integration',
        cellChanges: '/api/cell-changes',
        clients: '/api/clients',
        users: '/api/users',
        jobTemplates: '/api/job-templates'
      }
    }
  };

  if (isLocal) {
    // Local development - C# API runs on localhost:7126
    return {
      ...baseConfig,
      api: {
        ...baseConfig.api,
        baseUrl: typeof API_BASE_URL !== 'undefined' ? API_BASE_URL : 'https://localhost:7126'
      },
      urls: {
        redirectUri: typeof window !== 'undefined' ? window.location.origin : 'https://localhost:3000',
        digitalHub: 'https://digital-hub-uat.bdo.co.nz',
        standaloneOrigin: typeof window !== 'undefined' ? window.location.origin : 'https://localhost:3000'
      }
    } as Environment;
  } else if (isDev) {
    // Dev Azure deployment - URLs injected at build time or use defaults
    return {
      ...baseConfig,
      api: {
        ...baseConfig.api,
        baseUrl: typeof API_BASE_URL !== 'undefined' ? API_BASE_URL : ''
      },
      urls: {
        // Prefer the runtime origin so MSAL redirects back to whichever host
        // the user actually loaded us from — supports the Azure SWA hostname,
        // the custom dev domain, and any future domain aliases simultaneously
        // (all just need to be registered as SPA reply URIs on the Entra app).
        // ADDIN_URL is kept as a fallback for the SSR-like case where `window`
        // is undefined; in a real browser it never wins.
        redirectUri: typeof window !== 'undefined' ? window.location.origin : (typeof ADDIN_URL !== 'undefined' ? ADDIN_URL : ''),
        digitalHub: 'https://digital-hub-uat.bdo.co.nz',
        standaloneOrigin: 'https://wi-dev.bdo.co.nz'
      },
      appInsightsInstrumentationKey: typeof APPINSIGHTS_KEY !== 'undefined' ? APPINSIGHTS_KEY : undefined
    } as Environment;
  } else {
    // Production Azure deployment - URLs injected at build time or use defaults
    return {
      ...baseConfig,
      api: {
        ...baseConfig.api,
        baseUrl: typeof API_BASE_URL !== 'undefined' ? API_BASE_URL : ''
      },
      urls: {
        // See dev block above — runtime origin wins so MSAL supports the
        // SWA hostname, the custom prod domain, and any aliases at once.
        redirectUri: typeof window !== 'undefined' ? window.location.origin : (typeof ADDIN_URL !== 'undefined' ? ADDIN_URL : ''),
        digitalHub: 'https://digital-hub.bdo.co.nz',
        standaloneOrigin: 'https://wi.bdo.co.nz'
      },
      appInsightsInstrumentationKey: typeof APPINSIGHTS_KEY !== 'undefined' ? APPINSIGHTS_KEY : undefined
    } as Environment;
  }
}

export const environment = getEnvironment();